/**
 * useSupabaseScenes / useCreateSupabaseScene
 *
 * Sprint B Supabase-backed replacement for the read/create slice of the legacy
 * REST `useScene` hook. Queries `public.scenes` directly via the supabase-js
 * client and returns a row shape the UI can consume (a strict subset of
 * `SupabaseSceneRow` — see `types/supabase.ts`).
 *
 * Contract notes:
 *   - We deliberately return a Supabase-shaped row (snake_case `source_type`,
 *     `point_count`, `bounding_box`, `storage_path`, `created_at`) rather than
 *     the legacy camelCase `Scene` type from `types/index.ts`. Mapping to the
 *     legacy `Scene` (with `fileName` / `fileType`) is the responsibility of
 *     consumers / a future adapter, because the legacy `fileType` enum lives
 *     on `scene_files`, not `scenes`, and folding it in here would force a
 *     join on every list.
 *   - `useSupabaseScenes(null)` is a disabled query — no network call, no
 *     spinner. Mirrors the existing `useScene(null)` ergonomics.
 *   - `useCreateSupabaseScene` invalidates the matching list cache on success
 *     so the project view re-renders without a manual refetch.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSupabase } from '../lib/supabase/client';
import type {
  SceneBoundingBox,
  SceneSourceType,
  SupabaseSceneInsert,
  SupabaseSceneRow,
} from '../types/supabase';

const SCENES_TABLE = 'scenes';

/** Subset of {@link SupabaseSceneRow} the UI needs at the hook boundary. */
export interface SupabaseSceneListItem {
  id: string;
  project_id: string;
  name: string;
  source_type: SceneSourceType;
  point_count: number;
  bounding_box: SceneBoundingBox | null;
  storage_path: string | null;
  created_at: string;
  updated_at: string;
}

const SCENE_SELECT_COLUMNS =
  'id, project_id, name, source_type, point_count, bounding_box, storage_path, created_at, updated_at';

export function scenesQueryKey(projectId: string | null) {
  return ['supabase', 'scenes', projectId] as const;
}

export function useSupabaseScenes(projectId: string | null) {
  return useQuery<SupabaseSceneListItem[], Error>({
    queryKey: scenesQueryKey(projectId),
    enabled: !!projectId,
    staleTime: 60_000,
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from(SCENES_TABLE)
        .select(SCENE_SELECT_COLUMNS)
        .eq('project_id', projectId as string)
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message ?? 'Failed to load scenes');
      return (data ?? []) as SupabaseSceneListItem[];
    },
  });
}

export interface CreateSupabaseSceneInput {
  project_id: string;
  name: string;
  source_type?: SceneSourceType;
  storage_path?: string | null;
  point_count?: number;
  bounding_box?: SceneBoundingBox | null;
  metadata?: Record<string, unknown>;
}

export function useCreateSupabaseScene() {
  const queryClient = useQueryClient();

  return useMutation<SupabaseSceneListItem, Error, CreateSupabaseSceneInput>({
    mutationFn: async (input) => {
      const supabase = getSupabase();
      const row: SupabaseSceneInsert = {
        project_id: input.project_id,
        name: input.name,
        source_type: input.source_type ?? 'upload',
        point_count: input.point_count ?? 0,
        bounding_box: input.bounding_box ?? null,
        storage_path: input.storage_path ?? null,
        metadata: input.metadata ?? {},
      };

      const { data, error } = await supabase
        .from(SCENES_TABLE)
        .insert(row)
        .select(SCENE_SELECT_COLUMNS)
        .single();

      if (error) throw new Error(error.message ?? 'Failed to create scene');
      return data as SupabaseSceneListItem;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: scenesQueryKey(variables.project_id),
      });
    },
  });
}
